import React, { useState } from "react";
import { X, ChevronRight, ChevronLeft, ShieldCheck, Heart, Check } from "lucide-react";
import toast from "react-hot-toast";

const Questionnaire = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(0); // 0 = Privacy Consent
  const [answers, setAnswers] = useState({});

  const questions = [
    {
      id: 1,
      type: "single",
      q: "Are your periods regular?",
      sub: "By 'regular,' we mean the gap between your periods is roughly the same each month.",
      options: [
        { label: "Yes", note: "Nice! This helps us understand your cycle better." },
        { label: "No", note: "Totally okay. A lot of people have irregular cycles." },
        { label: "I don't know", note: "No worries at all. Your answer still helps us." }
      ]
    },
    {
      id: 2,
      type: "single",
      q: "How many days does your period cycle last?",
      options: [
        { label: "2-3 days" }, 
        { label: "3-5 days" }, 
        { label: "5-7 days" }, 
        { label: "More than 7 days" }
      ]
    },
    {
      id: 3,
      type: "single",
      q: "How do you feel about your periods?",
      options: [
        { label: "It's a love-hate relationship", note: "Some days it's okay, some days it's not." },
        { label: "Embarrassed", note: "Totally normal. You can explore and learn quietly." },
        { label: "Hate it", note: "Valid. You're not alone. We'll help make it easier." },
        { label: "I want to understand it better", note: "Let's decode your body together." },
        { label: "We've become friends", note: "We love that energy! Let's keep you in sync." }
      ]
    },
    {
      id: 4,
      type: "single",
      q: "How heavy is your flow at the start of your period?",
      sub: "(First 1–3 days)",
      options: [{ label: "Light" }, { label: "Medium" }, { label: "Heavy" }, { label: "Very Heavy" }]
    },
    {
      id: 5,
      type: "multi",
      q: "Do you experience any of these symptoms?",
      sub: "Choose all that apply.",
      options: [
        { label: "Cramps", note: "Super common and totally valid to mention." },
        { label: "Bloating", note: "That heavy, puffy feeling? It counts." },
        { label: "Mood swings", note: "Feeling extra emotional happens to many." },
        { label: "Headaches", note: "Annoying and exhausting, we know." },
        { label: "Fatigue", note: "Feeling more tired than usual is common." },
        { label: "Tender breasts", note: "This can happen and it's normal." },
        { label: "Backache", note: "Back pain can be part of period symptoms too." },
        { label: "Leg pain / heaviness", note: "Aches or heavy legs can show up with your cycle." },
        { label: "None", note: "Nothing comes to mind right now." }
      ]
    },
  ];

  const handleOptionClick = (optLabel) => {
    const currentQuestion = questions[step - 1];

    if (currentQuestion.type === "single") {
      setAnswers({ ...answers, [step]: optLabel });
      // Auto-advance for single choice
      if (step < questions.length) {
        setTimeout(() => setStep(step + 1), 300); // Small delay for visual feedback
      }
    } else {
      // Multi-select logic
      const currentSelections = answers[step] || [];
      if (currentSelections.includes(optLabel)) {
        setAnswers({ ...answers, [step]: currentSelections.filter((i) => i !== optLabel) });
      } else {
        setAnswers({ ...answers, [step]: [...currentSelections, optLabel] });
      }
    }
  };

  const handleComplete = () => {
    sessionStorage.setItem("quiz_completed", "true");
    toast.success("Recommendations generated!", { 
      id: "quiz-success", 
      position: "top-center",
      icon: '🌸',
      style: { borderRadius: '12px', background: '#333', color: '#fff', minWidth: '250px' }
    });
    onClose();
  };

  if (!isOpen) return null;

  const currentQ = questions[step - 1];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 font-sans">
      <div className="bg-white w-full max-w-lg rounded-[2.5rem] shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
        
        {/* Progress Header */}
        <div className="bg-pink-50 px-8 py-6 flex justify-between items-center border-b border-pink-100">
          <div>
            <h3 className="font-bold text-pink-800 flex items-center gap-2">
              <Heart size={18} className="fill-pink-500 text-pink-500" />
              Cycle Personalization
            </h3>
            <p className="text-[10px] font-bold text-pink-400 uppercase tracking-widest mt-1">
              {step === 0 ? "Privacy First" : `Step ${step} of ${questions.length}`}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white rounded-full transition-all text-gray-400"><X size={20}/></button>
        </div>

        <div className="p-8">
          {step === 0 ? (
            <div className="text-center">
              <div className="bg-pink-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="text-pink-600" size={32} />
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">Your Privacy Matters</h2>
              <p className="text-sm text-gray-600 mb-8 leading-relaxed">
                "Help us recommend products tailored to YOUR menstrual cycle." <br/>
                Your health data is encrypted and kept private.
              </p>
              <button 
                onClick={() => setStep(1)}
                className="w-full py-4 bg-gray-900 text-white rounded-2xl font-bold hover:bg-black transition-all shadow-lg active:scale-95"
              >
                Accept & Start
              </button>
            </div>
          ) : (
            <div>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-800 mb-2">{currentQ.q}</h2>
                <p className="text-sm text-gray-500 leading-relaxed">{currentQ.sub}</p>
              </div>
              
              <div className="space-y-3 mb-8 min-h-[300px] max-h-[400px] overflow-y-auto pr-2 scrollbar-hide">
                {currentQ.options.map((opt) => {
                  const isSelected = currentQ.type === "single" 
                    ? answers[step] === opt.label 
                    : answers[step]?.includes(opt.label);

                  return (
                    <button 
                      key={opt.label}
                      onClick={() => handleOptionClick(opt.label)}
                      className={`w-full p-4 text-left rounded-2xl border-2 transition-all flex justify-between items-start gap-3 ${
                        isSelected
                        ? "border-pink-500 bg-pink-50 text-pink-700 shadow-sm"
                        : "border-gray-50 hover:border-pink-200 hover:bg-pink-50 text-gray-700"
                      }`}
                    >
                      <div className="flex-1">
                        <p className="text-sm font-bold mb-0.5">{opt.label}</p>
                        <p className={`text-[11px] font-medium leading-tight ${isSelected ? "text-pink-500" : "text-gray-400"}`}>
                          {opt.note}
                        </p>
                      </div>
                      <div className={`mt-1 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? "bg-pink-500 border-pink-500" : "border-gray-200"}`}>
                        {isSelected && <Check size={12} className="text-white stroke-[3px]" />}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-gray-100 h-1.5 rounded-full mb-6">
                <div 
                  className="bg-pink-500 h-full rounded-full transition-all duration-500" 
                  style={{width: `${(step/questions.length)*100}%`}} 
                />
              </div>

              <div className="flex justify-between items-center">
                <button 
                  onClick={() => setStep(step - 1)}
                  className="text-gray-400 font-bold text-sm flex items-center gap-1 hover:text-pink-600 transition-colors"
                >
                  <ChevronLeft size={18}/> Back
                </button>
                
                <div className="flex gap-4 items-center">
                  <button onClick={onClose} className="text-gray-400 font-bold text-sm hover:text-gray-600">Skip</button>
                  
                  {(step === questions.length || currentQ.type === "multi") && (
                    <button 
                      onClick={() => step === questions.length ? handleComplete() : setStep(step + 1)}
                      className="bg-pink-500 text-white px-8 py-2.5 rounded-xl font-bold text-sm shadow-md hover:bg-pink-600 active:scale-95 transition-all"
                    >
                      {step === questions.length ? "Finish" : "Next"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Questionnaire;